import * as React from 'react';
import * as yup from 'yup';
import {useRef, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';

import {Alerts} from '../ui/Alerts/Alerts';
import {ContentModal} from '../ui/modals/ContentModal/ContentModal';
import {gaLegacyCustomEvent} from '../../client/ga/ga-legacy.functions';
import {ImpError} from '../../client/imp-error/imp-error.class';
import {Input} from '../ui/forms/Input/Input';
import {ListOperation} from './lists/lists.class';
import {ListsService} from '../../client/lists/lists.service';
import {ListType, ListTypeSelection} from './list.class';
import {ProgressOverlay} from '../../client/components/ProgressOverlay';
import {Radio} from '../ui/forms/Radio/Radio';
import {useService} from '../react/ServiceContext';
import {validateListName} from '../tools/yup-validators.functions';
import Button from '../ui/Buttons/Button';

interface CreateNewListFormValues {
    listName: string;
    listTypeSelection: ListTypeSelection;
}

interface CreateNewListModalProps {
    afterCreateNewListRes: (newListId: string, newListName: string) => void;
    listOperation: ListOperation;
    onClose: () => void;
    show: boolean;
    titleOverride?: string;
    tokenId?: string;
}

export const CreateNewListModal = ({
    afterCreateNewListRes,
    listOperation,
    onClose,
    show,
    titleOverride,
    tokenId,
}: CreateNewListModalProps) => {
    const [updateListErrMessage, setUpdateListErrMessage] = useState(``);
    const formRef = useRef(null);
    const listsService: ListsService = useService(`listsService`);
    const formValidation = yup.object().shape({
        listName: validateListName(),
    });
    const useFormReturn = useForm<CreateNewListFormValues>({
        defaultValues: {
            listName: listOperation.listName,
            listTypeSelection: `public`,
        },
        resolver: yupResolver(formValidation),
    });

    /**
     * TBD
     * @param createNewListForm
     */
    const submitCreateNewListForm = (createNewListForm: CreateNewListFormValues) => {
        let newListName = createNewListForm.listName;
        const listTypeSelection = createNewListForm.listTypeSelection;
        setUpdateListErrMessage(``);
        ProgressOverlay.activate();

        // Determine if it will be default list
        const defaultList = newListName.toLowerCase() === `my favorites` ? `yes` : `no`;
        newListName = defaultList === `yes` ? `` : newListName;

        // Determine listType
        let listType: ListType = ``;
        if (defaultList === `yes`) {
            listType = `d`;
        } else if (listTypeSelection === `private`) {
            listType = `p`;
        }

        // Create list
        listsService
            .updateList(listOperation.listId, listOperation.listAction, '', defaultList, newListName, listType, tokenId)
            .then((updateListRes) => {
                const newListId = updateListRes.ezid ? updateListRes.ezid.toString() : ``;

                // Invoke afterCreateNewListRes if provided
                if (afterCreateNewListRes) {
                    afterCreateNewListRes(newListId, newListName);
                }

                // Send analytics
                gaLegacyCustomEvent({eventAction: listOperation.listAction, eventCategory: `lists`, eventLabel: newListName});

                // Close modal
                onCloseHandler();
                ProgressOverlay.deactivate();
            })
            .catch((updateListErr: ImpError) => {
                ProgressOverlay.deactivate();
                setUpdateListErrMessage(updateListErr.message);
            });
        createNewListForm.listName = ``;
    };

    /**
     * Resets the state of the modal before closing
     */
    const onCloseHandler = () => {
        setUpdateListErrMessage(``);
        onClose();
    };

    /**
     * Template
     */
    return (
        <ContentModal
            footer={
                <>
                    <Button
                        e2e="cancelList"
                        modal={true}
                        modalOrderChangeButton1={true}
                        onClick={() => {
                            onClose();
                        }}
                        size="md"
                        variant="outline-secondary"
                    >
                        Cancel
                    </Button>
                    <Button
                        e2e="createList"
                        modal={true}
                        modalOrderChangeButton2={true}
                        onClick={() => {
                            formRef.current?.dispatchEvent(new Event(`submit`, {bubbles: true, cancelable: true}));
                        }}
                        size="md"
                        type="submit"
                        variant="secondary"
                    >
                        {titleOverride || `Create List`}
                    </Button>
                </>
            }
            onClose={onCloseHandler}
            show={show}
            title={titleOverride || `Create New List`}
        >
            <FormProvider {...useFormReturn}>
                <form
                    onSubmit={(event) => {
                        setUpdateListErrMessage(``);
                        useFormReturn
                            .handleSubmit(submitCreateNewListForm)(event)
                            .catch((handleSubmitErr) => {
                                setUpdateListErrMessage(handleSubmitErr.message);
                            });
                    }}
                    ref={formRef}
                >
                    <Alerts
                        message={updateListErrMessage}
                        variant="danger"
                    />
                    <div className="tw-text-sm tw-font-normal tw-leading-[1.3] tw-text-right">* required</div>
                    <Input
                        displayCounter={true}
                        error={useFormReturn?.formState?.errors?.listName?.message}
                        label="List Name"
                        maxLength={50}
                        name="listName"
                        required
                        {...useFormReturn.register(`listName`)}
                    />
                    <div>
                        <div className="tw-pb-2">Privacy</div>
                        <Radio
                            alias="public"
                            caption="All users on the account can see this list"
                            label="Public"
                            name="listTypeSelection"
                            value="public"
                            {...useFormReturn.register(`listTypeSelection`)}
                        />
                        <Radio
                            alias="private"
                            caption="Only you can see this list"
                            label="Private"
                            name="listTypeSelection"
                            value="private"
                            {...useFormReturn.register(`listTypeSelection`)}
                        />
                    </div>
                </form>
            </FormProvider>
        </ContentModal>
    );
};
